import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Plus, Minus, ShoppingBag, X } from "lucide-react";
import { useAuth0 } from "@auth0/auth0-react";

export default function CartPage() {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const AUTH_API = process.env.REACT_APP_AUTH0_API;

  // Use Auth0 hooks
  const { 
    user: auth0User, 
    isLoading: auth0Loading, 
    isAuthenticated, 
    logout, 
    getAccessTokenSilently 
  } = useAuth0();

  useEffect(() => {
    // Only fetch cart when Auth0 authentication is completed
    if (!auth0Loading && isAuthenticated && auth0User) {
      fetchCartItems(auth0User.email);
    }
  }, [auth0Loading, isAuthenticated, auth0User]);

  const fetchCartItems = async (email) => {
    setIsLoading(true);
    try {
      // Get token from Auth0
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: AUTH_API // Make sure this matches your backend API_IDENTIFIER
        }
      });
      
      const response = await fetch(`${API_BASE_URL}/cart/${email}/`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch cart items: ${response.status}`);
      }
      
      const data = await response.json();
      setCartItems(data);
      setError(null); // Clear any previous errors
    } catch (error) {
      console.error("Error fetching cart:", error);
      setError("Failed to load cart. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.total_price,
    0
  );
  const shippingCost = subtotal > 5000 ? 0 : 200;
  const total = subtotal;

  const increaseQuantity = async (cartId) => {
    try {
      const item = cartItems.find(item => item.cart_id === cartId);
      if (!item) return;
      
      const newQuantity = item.quantity + 1;
      
      // Get token from Auth0
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: AUTH_API
        }
      });
      
      const response = await fetch(`${API_BASE_URL}/cart/update/${cartId}/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          quantity: newQuantity
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update item quantity");
      }
      
      // Update local state immediately for better UX
      setCartItems(prevItems =>
        prevItems.map((item) =>
          item.cart_id === cartId ? 
            { 
              ...item, 
              quantity: newQuantity,
              total_price: newQuantity * item.unit_price 
            } 
            : item
        )
      );
    } catch (error) {
      console.error("Error updating quantity:", error);
      setError("Failed to update quantity. Please try again.");
      setModalMessage("Failed to update quantity. Please try again.");
      setShowModal(true);
    }
  };

  const decreaseQuantity = async (cartId) => {
    try {
      const item = cartItems.find(item => item.cart_id === cartId);
      if (!item || item.quantity <= 1) return;
      
      const newQuantity = item.quantity - 1;
      
      // Get token from Auth0
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: AUTH_API
        }
      });
      
      const response = await fetch(`${API_BASE_URL}/cart/update/${cartId}/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          quantity: newQuantity
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update item quantity");
      }
      
      // Update local state immediately for better UX
      setCartItems(prevItems =>
        prevItems.map((cartItem) =>
          cartItem.cart_id === cartId
            ? { 
                ...cartItem, 
                quantity: newQuantity,
                total_price: newQuantity * cartItem.unit_price
              }
            : cartItem
        )
      );
    } catch (error) {
      console.error("Error updating quantity:", error);
      setError("Failed to update quantity. Please try again.");
      setModalMessage("Failed to update quantity. Please try again.");
      setShowModal(true);
    }
  };

  const removeItem = async (cartId) => {
    try {
      // Get token from Auth0
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: AUTH_API
        }
      });
  
      // Get the user email
      const userEmail = auth0User.email;

      const response = await fetch(`${API_BASE_URL}/cart/remove/${cartId}/?email=${userEmail}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });
  
      if (!response.ok) {
        let errorMessage = "Failed to remove item";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (jsonError) {
          // If response is not JSON, use default message
          console.error("Error parsing error response:", jsonError);
        }
        throw new Error(errorMessage);
      }
      
      // Update local state immediately
      setCartItems(prevItems => prevItems.filter(item => item.cart_id !== cartId));
      setModalMessage("Item successfully removed from your cart!");
      setShowModal(true);
      
    } catch (error) {
      console.error("Error removing item:", error);
      setError(error.message || "Failed to remove item. Please try again.");
      setModalMessage(error.message || "Failed to remove item. Please try again.");
      setShowModal(true);
    }
  };

  const handlePurchaseNow = () => {
    // Check if the user is authenticated before proceeding
    if (!isAuthenticated) {
      setModalMessage("Please log in to complete your purchase.");
      setShowModal(true);
      return;
    }

    // Check if there are items in the cart
    if (cartItems.length === 0) {
      setModalMessage("Your cart is empty. Add items before proceeding to checkout.");
      setShowModal(true);
      return;
    }

    // Pass cart data and order summary to the billing page
    navigate("/billing", { 
      state: { 
        cartItems,
        orderSummary: {
          subtotal,
          shippingCost,
          total
        },
        userEmail: auth0User.email
      } 
    });
  };

  const handleRetry = () => {
    setError(null);
    if (auth0User && auth0User.email) {
      fetchCartItems(auth0User.email);
    }
  };

  // If still loading authentication
  if (auth0Loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, show login prompt
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-1 px-4 md:px-8 mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <h1
            className="text-3xl md:text-4xl text-[#333333] mb-8 relative"
            style={{ fontFamily: "serif" }}
          >
            Your Cart
            <span className="block w-24 h-1 bg-[#D4AF37] mx-auto mt-2"></span>
          </h1>
          <p className="text-gray-600 text-lg mb-6">
            Please log in to view your cart and checkout.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center bg-[#D4AF37] text-black px-6 py-2 rounded-sm text-sm font-medium hover:bg-[#C19B2E] transition-all duration-200"
          >
            Log In
          </Link>
        </div>
      </div>
    );
  }

  // If there's an error
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-1 px-4 md:px-8 mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <h1
            className="text-3xl md:text-4xl text-[#333333] mb-8 relative"
            style={{ fontFamily: "serif" }}
          >
            Your Cart
            <span className="block w-24 h-1 bg-[#D4AF37] mx-auto mt-2"></span>
          </h1>
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={handleRetry}
            className="inline-flex items-center bg-[#D4AF37] text-black px-6 py-2 rounded-sm text-sm font-medium hover:bg-[#C19B2E] transition-all duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-1 px-4 md:px-8 mt-12">
      <div className="max-w-7xl mx-auto">
        <h1
          className="text-3xl md:text-4xl text-[#333333] text-center mb-8 relative"
          style={{ fontFamily: "serif" }}
        >
          Your Cart
          <span className="block w-24 h-1 bg-[#D4AF37] mx-auto mt-2"></span>
        </h1>

        {isLoading ? (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your cart...</p>
          </div>
        ) : cartItems.length === 0 ? (
          <div className="text-center">
            <p className="text-gray-600 text-lg mb-4">
              Your cart is empty.
            </p>
            <Link
              to="/shop"
              className="inline-flex items-center bg-[#D4AF37] text-black px-6 py-2 rounded-sm text-sm font-medium hover:bg-[#C19B2E] transition-all duration-200"
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              Start Shopping
            </Link>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex-1">
              <h2
                className="text-2xl text-[#333333] mb-6"
                style={{ fontFamily: "serif" }}
              >
                Cart Items ({cartItems.length})
              </h2>
              <div className="space-y-6">
                {cartItems.map((item) => (
                  <div
                    key={item.cart_id}
                    className="flex flex-col md:flex-row items-center bg-white p-4 rounded-sm border border-gray-200 shadow-sm"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-24 h-24 object-cover rounded-sm mb-4 md:mb-0 md:mr-4"
                      onError={(e) => {
                        e.target.src = '/placeholder-image.jpg'; // Fallback image
                      }}
                    />
                    <div className="flex-1 text-center md:text-left">
                      <h3 className="text-lg text-[#333333] font-medium">
                        {item.name}
                      </h3>
                      <p className="text-sm text-gray-500">Size: {item.size}</p>
                      <p className="text-sm text-gray-600">
                        ₹{item.unit_price} / unit
                      </p>
                    </div>
                    <div className="flex items-center gap-2 my-4 md:my-0">
                      <button
                        onClick={() => decreaseQuantity(item.cart_id)}
                        disabled={item.quantity <= 1}
                        className={`p-1 rounded-sm border transition-colors ${
                          item.quantity <= 1 
                            ? 'border-gray-200 bg-gray-100 cursor-not-allowed' 
                            : 'border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        <Minus className={`w-4 h-4 ${
                          item.quantity <= 1 ? 'text-gray-400' : 'text-gray-600'
                        }`} />
                      </button>
                      <span className="text-sm text-gray-600 min-w-[2rem] text-center">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => increaseQuantity(item.cart_id)}
                        className="p-1 rounded-sm border border-gray-300 hover:bg-gray-100 transition-colors"
                      >
                        <Plus className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                    <div className="text-sm text-gray-600 md:ml-4 font-medium">
                      Total: ₹{item.total_price}
                    </div>
                    <button
                      onClick={() => removeItem(item.cart_id)}
                      className="ml-4 text-red-500 hover:text-red-600 p-1 rounded-sm hover:bg-red-50 transition-colors"
                      title="Remove item"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-6 text-center md:text-left">
                <Link
                  to="/shop"
                  className="inline-flex items-center bg-gray-200 text-[#333333] px-4 py-2 rounded-sm text-sm font-medium hover:bg-gray-300 transition-all duration-200"
                >
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Add More Items
                </Link>
              </div>
            </div>

            <div className="md:w-80">
              <div className="bg-white p-6 rounded-sm border border-gray-200 shadow-sm">
                <h2
                  className="text-2xl text-[#333333] mb-4"
                  style={{ fontFamily: "serif" }}
                >
                  Invoice Summary
                </h2>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Subtotal</span>
                    <span>₹{subtotal.toLocaleString()}</span>
                  </div>
                  {/* <div className="flex justify-between text-sm text-gray-600">
                    <span>Shipping</span>
                    <span>{shippingCost === 0 ? "Free" : `₹${shippingCost}`}</span>
                  </div> */}
                  {subtotal > 5000 && (
                    <div className="text-xs text-green-600">
                      Free shipping on orders over ₹5,000!
                    </div>
                  )}
                  <div className="border-t border-gray-200 my-2"></div>
                  <div className="flex justify-between text-lg font-medium text-[#333333]">
                    <span>Total</span>
                    <span>₹{total.toLocaleString()}</span>
                  </div>
                </div>
                <button
                  onClick={handlePurchaseNow}
                  className={`w-full mt-6 px-6 py-3 rounded-sm text-sm font-medium transition-all duration-200 ${
                    cartItems.length === 0 
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                      : 'bg-[#D4AF37] text-black hover:bg-[#C19B2E]'
                  }`}
                  disabled={cartItems.length === 0}
                >
                  Purchase Now
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <Modal 
          message={modalMessage} 
          onClose={() => setShowModal(false)} 
        />
      )}
    </div>
  );
}

const Modal = ({ message, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-sm max-w-sm w-full mx-4 shadow-lg">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl text-[#333333]" style={{ fontFamily: "serif" }}>
            Notification
          </h3>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700 p-1 rounded-sm hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="mb-6">
          <p className="text-gray-600">{message}</p>
        </div>
        <div className="text-right">
          <button 
            onClick={onClose}
            className="bg-[#D4AF37] text-black px-4 py-2 rounded-sm text-sm font-medium hover:bg-[#C19B2E] transition-all duration-200"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};
